import type { ComponentProps } from 'react';
import { KeyboardShortcut } from '@gonfalon/keyboard-shortcuts/ui';
import { ListBoxItem, Text } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { useCommandPaletteState } from '../hooks/use-command-palette-state';
import type { Command } from '../types/command';
import { getDisplayLabel } from '../utils/get-display-label';

import { useCommandRegistry } from './command-registry-provider';
import { getTypeIcon } from './get-type-icon';

import styles from './command-palette-list-box-item.module.css';

interface CommandPaletteListBoxItemProps {
  command: Command;
}

export function CommandPaletteListBoxItem({ command }: CommandPaletteListBoxItemProps) {
  const { commands } = useCommandRegistry();
  const { rootCommandId } = useCommandPaletteState();
  const { label, parentLabel } = getDisplayLabel(command, commands, rootCommandId);

  const itemProps: ComponentProps<typeof ListBoxItem> = {
    id: command.id,
    textValue: command.label,
  };

  if (command.type === 'link') {
    itemProps.href = command.href;
    itemProps.target = command.newTab ? '_blank' : undefined;
  }

  // Rows render plain text -- no per-character highlight of the
  // matched substring. Reasoning: only `label` and `description` are
  // visible, but commands match via several hidden surfaces too
  // (`keywords`, Fuse fuzzy similarity, and server-side filtering
  // narrowing the candidate set before we even see it). A highlight
  // that's sometimes present and sometimes absent is more confusing
  // than helpful; we trust ranking + position to communicate match
  // quality instead. Matches Linear / kbar conventions.
  return (
    <ListBoxItem {...itemProps}>
      <Text slot="label">
        <Icon name={command.icon || getTypeIcon(command.type)} size="small" />
        <div>
          {parentLabel !== undefined && (
            // Deep-search hit: the user typed a query that surfaced this
            // command from inside another scope. Prefix the parent's
            // label so the row's place in the tree is visible.
            // Rendered with `aria-hidden` so screen readers read the
            // semantically-meaningful label (`textValue`) without the
            // breadcrumb prefix, which is decorative.
            <span className={styles.breadcrumb} aria-hidden>
              {parentLabel}
              {' \u203a '}
            </span>
          )}
          {label}
        </div>
      </Text>
      {command.description && (
        // Cap the description at two lines with an ellipsis. Consumers
        // routinely pack long-form text in here (a flag's description
        // field can be paragraphs of HTML-flavoured prose with links
        // and headings); without a cap, a single row can balloon to
        // a half-screen of wrapped text and push everything else out
        // of view. The full text is still in the DOM (just visually
        // clipped) so screen readers and copy-paste survive.
        //
        // `styles.description` adds `overflow-wrap: anywhere` so
        // tokens with no internal whitespace -- long flag keys,
        // URLs, UUIDs -- break to fit the row width instead of
        // overflowing.
        <Text slot="description" maxLines={2} className={styles.description}>
          {command.description}
        </Text>
      )}
      {command.shortcut && <KeyboardShortcut shortcut={command.shortcut} />}
    </ListBoxItem>
  );
}
